import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Pill,
  Calendar,
  User,
  CheckCircle,
  AlertTriangle,
  Clock,
  X,
  RotateCcw,
  FileText,
  DollarSign,
  Send,
} from "lucide-react";
import {
  Subscription,
  getEffectiveStatus,
  getStatusColor,
  getStatusLabel,
  getDaysUntil,
} from "@/lib/subscriptionsData";

// Helper UI functions (defined outside to avoid re-creation on each render)
const getPaymentIcon = (status: string) => {
  switch (status) {
    case "paid":
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    case "pending":
      return <Clock className="w-4 h-4 text-yellow-600" />;
    case "failed":
      return <X className="w-4 h-4 text-red-600" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-gray-600" />;
  }
};

const getPaymentBadgeColor = (status: string) => {
  switch (status) {
    case "paid":
      return "bg-green-100 text-green-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "failed":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

interface ManageSubscriptionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription | null;
  isDarkMode: boolean;
}

export function ManageSubscriptionModal({
  isOpen,
  onOpenChange,
  subscription,
  isDarkMode,
}: ManageSubscriptionModalProps) {
  const [activeTab, setActiveTab] = useState("next-issue");

  // Compute values safely regardless of subscription presence to maintain hook order
  const effectiveStatus = subscription
    ? getEffectiveStatus(subscription)
    : "awaiting_payment";
  const statusColor = getStatusColor(effectiveStatus);
  const statusLabel = getStatusLabel(effectiveStatus);

  const issues = subscription?.issueHistory ?? [];

  const { totalPaid, pendingAmount, failedAmount } = useMemo(() => {
    return issues.reduce(
      (acc, issue) => {
        if (issue.paymentStatus === "paid") acc.totalPaid += issue.amount;
        else if (issue.paymentStatus === "pending")
          acc.pendingAmount += issue.amount;
        else if (issue.paymentStatus === "failed")
          acc.failedAmount += issue.amount;
        return acc;
      },
      { totalPaid: 0, pendingAmount: 0, failedAmount: 0 }
    );
  }, [issues]);

  // Calculate next issue details
  const nextIssueNumber = (subscription?.currentIssue ?? 0) + 1;
  const daysUntilNextSupply = subscription
    ? getDaysUntil(subscription.nextSupplyDate)
    : 0;
  const nextSupplyDate = subscription
    ? new Date(subscription.nextSupplyDate).toLocaleDateString("en-GB")
    : "";

  // Check if payment has been made for next issue
  const hasNextPayment = useMemo(() => {
    return issues.some(
      (issue) =>
        issue.issueNumber === nextIssueNumber && issue.paymentStatus === "paid"
    );
  }, [issues, nextIssueNumber]);

  const handleSendReminder = () => {
  };

  const handleApproveNextSupply = () => {
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-4xl max-h-[90vh] overflow-y-auto ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white"
        }`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <RotateCcw className="w-5 h-5 text-blue-600" />
            Manage Subscription
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {subscription && (
            <>
              <div
                className={`p-6 rounded-lg border ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      <div>
                        <h3
                          className={`font-semibold ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {subscription.patientName}
                        </h3>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          NHS: {subscription.patientNHS}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Pill className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4
                          className={`font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {subscription.medication}
                        </h4>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {subscription.dosage}
                        </p>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Quantity: {subscription.quantity}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Prescribed by {subscription.prescribedBy}
                        </p>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {new Date(
                            subscription.consultationDate
                          ).toLocaleDateString("en-GB")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4
                        className={`font-medium mb-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Subscription Status
                      </h4>
                      <Badge className={`${statusColor} mb-2`}>
                        {statusLabel}
                      </Badge>
                      <div
                        className={`text-sm ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        <p>
                          Issues: {subscription.currentIssue}/
                          {subscription.totalIssues}
                        </p>
                        <p>Repeat: {subscription.repeatPeriod}</p>
                        <p>
                          Price per issue: £
                          {subscription.pricePerIssue.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div
                        className={`p-3 rounded ${
                          isDarkMode ? "bg-gray-600" : "bg-white"
                        } border`}
                      >
                        <p className="text-lg font-semibold text-green-600">
                          £{totalPaid.toFixed(2)}
                        </p>
                        <p
                          className={`text-xs ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Total Paid
                        </p>
                      </div>
                      {pendingAmount > 0 && (
                        <div
                          className={`p-3 rounded ${
                            isDarkMode ? "bg-gray-600" : "bg-white"
                          } border`}
                        >
                          <p className="text-lg font-semibold text-yellow-600">
                            £{pendingAmount.toFixed(2)}
                          </p>
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            Pending
                          </p>
                        </div>
                      )}
                      {failedAmount > 0 && (
                        <div
                          className={`p-3 rounded ${
                            isDarkMode ? "bg-gray-600" : "bg-white"
                          } border`}
                        >
                          <p className="text-lg font-semibold text-red-600">
                            £{failedAmount.toFixed(2)}
                          </p>
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            Failed
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList
                  className={`grid w-full grid-cols-2 ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <TabsTrigger
                    value="next-issue"
                    className={
                      isDarkMode ? "data-[state=active]:bg-gray-600" : ""
                    }
                  >
                    Next Issue
                  </TabsTrigger>
                  <TabsTrigger
                    value="issue-history"
                    className={
                      isDarkMode ? "data-[state=active]:bg-gray-600" : ""
                    }
                  >
                    Issue History
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="next-issue" className="space-y-6">
                  <div
                    className={`p-6 rounded-lg border ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <h3
                      className={`text-lg font-semibold mb-4 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Next Supply Details
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          <div>
                            <p
                              className={`font-medium ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              Issue #{nextIssueNumber}
                            </p>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Due: {nextSupplyDate}
                            </p>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {daysUntilNextSupply > 0
                                ? `${daysUntilNextSupply} days remaining`
                                : daysUntilNextSupply === 0
                                ? "Due today"
                                : `${Math.abs(
                                    daysUntilNextSupply
                                  )} days overdue`}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <DollarSign className="w-5 h-5 text-blue-600" />
                          <div>
                            <p
                              className={`font-medium ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              £{subscription.pricePerIssue.toFixed(2)}
                            </p>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Amount due
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Pill className="w-5 h-5 text-blue-600" />
                          <div>
                            <p
                              className={`font-medium ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {subscription.medication}
                            </p>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {subscription.dosage} - {subscription.quantity}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div
                          className={`p-6 rounded-lg border text-center ${
                            isDarkMode
                              ? "bg-gray-600 border-gray-500"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          <div className="flex justify-center mb-3">
                            {hasNextPayment ? (
                              <CheckCircle className="w-12 h-12 text-green-600" />
                            ) : (
                              <Clock className="w-12 h-12 text-yellow-600" />
                            )}
                          </div>
                          <h4
                            className={`text-lg font-semibold mb-2 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            Payment Status
                          </h4>
                          <Badge
                            className={
                              hasNextPayment
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }
                          >
                            {hasNextPayment
                              ? "Payment Received"
                              : "Payment Pending"}
                          </Badge>
                        </div>

                        <div className="space-y-3">
                          <Button
                            variant="outline"
                            className="w-full flex items-center gap-2"
                            onClick={handleSendReminder}
                          >
                            <Send className="w-4 h-4" />
                            Send Reminder
                          </Button>

                          {!hasNextPayment && (
                            <Button
                              className="w-full bg-green-600 hover:bg-green-700 flex items-center gap-2"
                              onClick={handleApproveNextSupply}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve Next Supply
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="issue-history">
                  <div
                    className={`rounded-lg border ${
                      isDarkMode ? "border-gray-600" : "border-gray-200"
                    }`}
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead
                          className={`${
                            isDarkMode ? "bg-gray-700" : "bg-gray-50"
                          }`}
                        >
                          <tr>
                            <th
                              className={`px-4 py-3 text-left text-xs font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-500"
                              } uppercase tracking-wider`}
                            >
                              Issue #
                            </th>
                            <th
                              className={`px-4 py-3 text-left text-xs font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-500"
                              } uppercase tracking-wider`}
                            >
                              Supply Date
                            </th>
                            <th
                              className={`px-4 py-3 text-left text-xs font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-500"
                              } uppercase tracking-wider`}
                            >
                              Amount
                            </th>
                            <th
                              className={`px-4 py-3 text-left text-xs font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-500"
                              } uppercase tracking-wider`}
                            >
                              Payment Status
                            </th>
                            <th
                              className={`px-4 py-3 text-left text-xs font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-500"
                              } uppercase tracking-wider`}
                            >
                              Payment Method
                            </th>
                            <th
                              className={`px-4 py-3 text-left text-xs font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-500"
                              } uppercase tracking-wider`}
                            >
                              Notes
                            </th>
                          </tr>
                        </thead>
                        <tbody
                          className={`${
                            isDarkMode ? "bg-gray-800" : "bg-white"
                          } divide-y ${
                            isDarkMode ? "divide-gray-700" : "divide-gray-200"
                          }`}
                        >
                          {subscription.issueHistory.map((issue) => (
                            <tr
                              key={issue.issueNumber}
                              className={`hover:${
                                isDarkMode ? "bg-gray-700" : "bg-gray-50"
                              }`}
                            >
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                  <span
                                    className={`text-sm font-medium ${
                                      isDarkMode
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {issue.issueNumber}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span
                                  className={`text-sm ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {new Date(
                                    issue.supplyDate
                                  ).toLocaleDateString("en-GB")}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span
                                  className={`text-sm font-medium ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  £{issue.amount.toFixed(2)}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  {getPaymentIcon(issue.paymentStatus)}
                                  <Badge
                                    className={getPaymentBadgeColor(
                                      issue.paymentStatus
                                    )}
                                  >
                                    {issue.paymentStatus
                                      .charAt(0)
                                      .toUpperCase() +
                                      issue.paymentStatus.slice(1)}
                                  </Badge>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span
                                  className={`text-sm ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {issue.paymentMethod || "-"}
                                </span>
                              </td>
                              <td className="px-4 py-4">
                                <span
                                  className={`text-sm ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {issue.notes || "-"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                {effectiveStatus === "consultation_required" && (
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Schedule Consultation
                  </Button>
                )}
                {(effectiveStatus === "awaiting_payment" ||
                  effectiveStatus === "overdue") && (
                  <Button className="bg-green-600 hover:bg-green-700">
                    Retry Payment
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                >
                  Pause Subscription
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
